import { Component, Host, h, Prop, Watch, Element } from "@stencil/core";

import type { PageConfig } from "../../../config/page-configs/page-configs";
import type { BotId } from "../../../global/models/models";
import type { AnswerBy, Conversation } from "../../../utils/backend/reply-parsed";
import { trapFocus } from "../../../utils/trap-focus/trap-focus";

@Component({
    tag: "c-dialog",
    styleUrl: "c-dialog.scss",
    shadow: true,
})
export class CDialog {
    @Prop() isOpen = false;
    @Prop() timeout = false;
    @Prop() loading = false;
    @Prop() showWelcome = true;
    @Prop() pageConfig: PageConfig;
    @Prop() conversation: Conversation;
    @Prop() currentBotId: BotId;
    @Prop() answerBy: AnswerBy;
    @Prop() trapFocus: any;
    @Prop() isLocal?: boolean = false

    @Element() hostElement: HTMLElement;

    @Watch("isOpen")
    isOpenChanged(isOpen: any) {
        if (isOpen) {
            document.addEventListener('keydown', this.trapFocus)
            this.preventBodyScroll();
        } else {
            document.removeEventListener('keydown', this.trapFocus)
            this.allowBodyScroll();
        }
    }

    constructor() {
        this.trapFocus = trapFocus(this.hostElement)
    }
    preventBodyScroll() {
        // We have to prevent the body from scrolling, when our bot is full screen. Otherwise there are two scrollbars.
        // One scrollbar for our bot and one for the body. This messes up our paddings in the message screen. Also on a real
        // phone, sometimes the bot is not scrollable, because you are actually scrolling the hidden website behind.
        const fullWidth = window.innerWidth <= 767 || window.innerHeight <= 365; // Future Task Adapt to new media queries
        if (fullWidth) {
            document.body.style.overflow = "hidden";
        }
    }

    allowBodyScroll() {
        document.body.style.overflow = "auto";
    }

    getInput() {
        if (!this.answerBy) {
            return <c-input-text disabled={true}></c-input-text>;
        }

        switch (this.answerBy.type) {
            case "cookies":
                return <c-input-text disabled={true}></c-input-text>;
            case "multipleChoice":
            case "BBOT": {
                const isDisabled = this.answerBy.inputDisabled !== false;
                return <c-input-text disabled={isDisabled}></c-input-text>;
            }
            case "dropdown":
                return <c-input-text></c-input-text>;
            case "suggestions":
            case "textInput":
                return (
                    <c-input-text
                        placeholder={this.answerBy.placeholder} botId={this.currentBotId}
                    ></c-input-text>
                );
            case "salaryInput":
                return (
                    <c-input-salary
                        placeholder={this.answerBy.placeholder}
                    ></c-input-salary>
                );
        }
    }

    render() {
        return (
            <Host
                id="cb-digitaler-assistent"
                aria-label={this.pageConfig.header}
                role="dialog"
                aria-describedby="cb-welcome-text"
                aria-hidden={this.isOpen ? null : "true"}
                class={this.isOpen ? "" : "closed"}
            >
                <c-header
                    botId={this.currentBotId}
                    pageConfig={this.pageConfig}
                    isWelcomeScreen={this.showWelcome}
                    isLocal={this.isLocal}
                    class={this.showWelcome ? "glow-unset" : "glow-inset"}
                ></c-header>
                {this.showWelcome ? (
                    <c-welcome pageConfig={this.pageConfig}></c-welcome>
                ) : (
                    <c-conversation
                        loading={this.loading}
                        timeout={this.timeout}
                        currentBotId={this.currentBotId}
                        conversation={this.conversation}
                        answerBy={this.answerBy}
                        formLang={localStorage.getItem("FormLang") || undefined}
                        formType={localStorage.getItem("StartFormular") || undefined}
                        formState={localStorage.getItem("FormState") || undefined}
                    ></c-conversation>
                )}
                {!this.showWelcome && this.getInput()}
            </Host>
        );
    }
}
